/**
 * 🔐 CLOUD USER SERVICE
 * Replaces problematic client-side user creation with safe Cloud Function calls
 * 
 * This service calls the createUserForOrg Cloud Function which uses Firebase Admin SDK
 * to create users without disrupting the current admin session.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { app } from '../config/firebase';

const functions = getFunctions(app, 'australia-southeast2');

/**
 * 🚨 SAFE USER CREATION: Create Firebase Auth user via Cloud Function
 * 
 * This replaces the dangerous client-side createUserWithEmailAndPassword
 * that was causing session swaps and permissions failures.
 * 
 * @param {string} email - User email address
 * @param {string} displayName - User full name  
 * @param {string} orgId - Organization ID
 * @param {Array} assignedCmtIds - CMT assignments (optional)
 * @param {string} role - User role (defaults to 'org_user')
 * 
 * @returns {Promise<Object>} Created user details and invitation info
 */
export const createUserForOrganization = async (email, displayName, orgId, assignedCmtIds = [], role = 'org_user', options = {}) => {
  try {
    console.log(`🔐 CLOUD USER SERVICE: Creating user via Cloud Function`, {
      email,
      displayName,
      orgId,
      assignedCmtIds,
      role,
      options
    });

    // Call the Cloud Function
    const createUserFunction = httpsCallable(functions, 'createUserForOrg');
    
    const result = await createUserFunction({
      email,
      displayName,
      orgId,
      assignedCmtIds,
      role,
      ...options
    });

    console.log('✅ CLOUD USER SERVICE: User created successfully', result.data);

    return {
      success: true,
      user: result.data.user,
      passwordResetLink: result.data.passwordResetLink,
      message: result.data.message,
      duration: result.data.duration
    };

  } catch (error) {
    console.error('❌ CLOUD USER SERVICE: User creation failed', error);

    // Extract meaningful error message
    let errorMessage = 'User creation failed';
    if (error.code === 'functions/permission-denied') {
      errorMessage = 'Insufficient permissions - only super admins can create users';
    } else if (error.code === 'functions/already-exists') {
      errorMessage = error.message || 'Email is already registered to another organization';
    } else if (error.code === 'functions/invalid-argument') {
      errorMessage = error.message || 'Invalid input parameters';
    } else if (error.code === 'functions/not-found') {
      errorMessage = error.message || 'Organization not found';
    } else if (error.code === 'functions/failed-precondition') {
      errorMessage = error.message || 'Organization is not active';
    } else if (error.message) {
      errorMessage = error.message;
    }

    throw new Error(errorMessage);
  }
};

/**
 * 📧 Send invitation email using password reset link
 * 
 * This function is called after user creation to send the actual invitation email.
 * The Cloud Function generates the password reset link, but we can also send
 * a custom invitation email here if needed.
 * 
 * @param {string} email - User email
 * @param {string} userName - User display name
 * @param {string} orgName - Organization name
 * @param {string} inviterName - Name of person sending invite
 * @param {string} passwordResetLink - Reset link from Cloud Function
 * 
 * @returns {Promise<Object>} Email send result
 */
export const sendInvitationEmail = async (email, userName, orgName, inviterName, passwordResetLink = null) => {
  try {
    console.log(`📧 CLOUD USER SERVICE: Sending invitation email`, {
      email,
      userName,
      orgName,
      hasResetLink: !!passwordResetLink
    });

    // Send the actual password reset email via Firebase Auth
    const auth = getAuth();
    await sendPasswordResetEmail(auth, email);

    console.log('✅ CLOUD USER SERVICE: Password reset email dispatched');

    return {
      success: true,
      message: `Invitation sent to ${userName}`,
      email: email,
      passwordResetLink: passwordResetLink || null
    };

  } catch (error) {
    console.error('❌ CLOUD USER SERVICE: Invitation email failed', error);
    throw new Error(`Failed to send invitation: ${error.message}`);
  }
};

/**
 * 🔄 Update user invitation status
 * 
 * Updates the user document in Firestore with invitation status
 * 
 * @param {string} userId - User document ID
 * @param {string} status - New status
 * @param {Object} metadata - Additional metadata
 * 
 * @returns {Promise<Object>} Update result
 */
export const updateInvitationStatus = async (userId, status, metadata = {}) => {
  try {
    console.log(`🔄 CLOUD USER SERVICE: Updating invitation status`, {
      userId,
      status,
      metadata
    });

    // Import here to avoid circular dependencies
    const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../config/firebase');

    const userRef = doc(db, 'c3_users', userId);
    
    const updateData = {
      status: status,
      invitationStatus: status,
      updatedAt: serverTimestamp(),
      ...metadata
    };
    
    if (status === 'invited') {
      updateData.invitedAt = serverTimestamp();
    }
    
    await updateDoc(userRef, updateData);
    
    console.log('✅ CLOUD USER SERVICE: Status updated successfully');
    
    return { success: true };
    
  } catch (error) {
    console.error('❌ CLOUD USER SERVICE: Status update failed', error);
    throw new Error(`Failed to update status: ${error.message}`);
  }
};

/**
 * 🛟 Fallback: update invitation status by email if userId is unknown or stale
 */
export const updateInvitationStatusByEmail = async (email, status, metadata = {}) => {
  try {
    const normalizedEmail = (email || '').toLowerCase().trim();
    if (!normalizedEmail) throw new Error('Missing email');

    const { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../config/firebase');

    const usersRef = collection(db, 'c3_users');
    const q = query(usersRef, where('email', '==', normalizedEmail));
    const snap = await getDocs(q);
    if (snap.empty) throw new Error('User not found by email');

    const target = snap.docs[0];
    const userRef = doc(db, 'c3_users', target.id);
    const updateData = {
      status,
      invitationStatus: status,
      updatedAt: serverTimestamp(),
      ...metadata
    };
    if (status === 'invited') updateData.invitedAt = serverTimestamp();

    await updateDoc(userRef, updateData);
    return { success: true };
  } catch (error) {
    console.error('❌ CLOUD USER SERVICE: Status update by email failed', error);
    throw new Error(`Failed to update status by email: ${error.message}`);
  }
};

/**
 * 🧪 Batch user creation for CMT sync
 * 
 * Creates multiple users efficiently using the Cloud Function
 * 
 * @param {Array} userList - List of users to create
 * @param {string} orgId - Organization ID
 * @param {Object} options - Additional options
 * 
 * @returns {Promise<Object>} Batch creation results
 */
export const createUsersBatch = async (userList, orgId, options = {}) => {
  const results = {
    successful: [],
    failed: [],
    total: userList.length
  };
  
  try {
    console.log(`👥 CLOUD USER SERVICE: Batch creating ${userList.length} users`);
    
    // Process users sequentially to avoid overwhelming the Cloud Function
    for (const [index, userData] of userList.entries()) {
      try {
        console.log(`👤 Creating user ${index + 1}/${userList.length}: ${userData.email}`);
        
        const result = await createUserForOrganization(
          userData.email,
          userData.fullName,
          orgId,
          userData.assignedCmtIds || [],
          userData.role || 'org_user'
        );
        
        results.successful.push({
          index,
          email: userData.email,
          uid: result.user.uid,
          result
        });
        
        // Small delay to prevent rate limiting
        if (index < userList.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
      } catch (error) {
        console.error(`❌ Failed to create user ${index + 1}: ${userData.email}`, error);
        
        results.failed.push({
          index,
          email: userData.email,
          error: error.message
        });
      }
    }
    
    console.log(`✅ CLOUD USER SERVICE: Batch complete - ${results.successful.length} successful, ${results.failed.length} failed`);
    
    return results;
    
  } catch (error) {
    console.error('❌ CLOUD USER SERVICE: Batch creation failed', error);
    throw new Error(`Batch user creation failed: ${error.message}`);
  }
};

/**
 * 🚨 Export default service object
 */
export default {
  createUserForOrganization,
  sendInvitationEmail,
  updateInvitationStatus,
  updateInvitationStatusByEmail,
  createUsersBatch
};
